import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Dialog, IconButton, Stack, Typography} from 'views/components';
import {Close} from 'views/icons';
import {Translate, translate} from 'views/module';

import SkillsNewGroup from './skills_new_components/SkillsNewGroup';
import SkillsNewName from './skills_new_components/SkillsNewName';
import SkillsNewPrompt from './skills_new_components/SkillsNewPrompt';

const SkillsNew = (props) => {
  const {active, groupOptions, onClose, handleCreateSkill, skill} = props;

  const functionOptions = translate('skills.skills.New.FunctionOptions');
  const hasExistingGroups = groupOptions.length > 0;
  const defaultGroupMode = hasExistingGroups ? 'existing' : 'new';
  const defaultExistingGroup = hasExistingGroups ? groupOptions[0] : '';

  const [groupMode, setGroupMode] = React.useState(defaultGroupMode);
  const [existingGroup, setExistingGroup] = React.useState(defaultExistingGroup);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [skillName, setSkillName] = React.useState('');
  const [selectedFunction, setSelectedFunction] = React.useState('');
  const [parameters, setParameters] = React.useState([]);
  const [prompt, setPrompt] = React.useState('');

  const groupName = groupMode === 'existing' ? existingGroup : newGroupName.trim();
  const isSubmitDisabled = !(skillName && selectedFunction && prompt && groupName);

  const existingSkillRn = skill?.skillRn || null;
  const submitButtonExpression = existingSkillRn ? 'skills.skills.New.Edit' : 'skills.skills.New.Create';

  const resetForm = React.useCallback(() => {
    setGroupMode(defaultGroupMode);
    setExistingGroup(defaultExistingGroup);
    setNewGroupName('');
    setSkillName('');
    setSelectedFunction('');
    setParameters([]);
    setPrompt('');
  }, [defaultExistingGroup, defaultGroupMode]);

  const populateForm = React.useCallback((currentSkill) => {
    setGroupMode('existing');
    setExistingGroup(currentSkill.groupName);
    setNewGroupName('');
    setSkillName(currentSkill.name || '');
    setSelectedFunction(currentSkill.outputType || '');
    setParameters(currentSkill.parameters || []);
    setPrompt(currentSkill.prompt || '');
  }, []);

  const closeDialog = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(() => {
    if (isSubmitDisabled) {
      return;
    }

    const skillPayload = {
      outputType: selectedFunction,
      name: skillName,
      parameters,
      prompt,
      groupName,
    };

    handleCreateSkill(skillPayload, existingSkillRn);
  }, [existingSkillRn, groupName, isSubmitDisabled, handleCreateSkill, parameters, prompt, selectedFunction, skillName]);

  React.useEffect(() => {
    if (!active) {
      resetForm();
      return;
    }

    if (skill) {
      populateForm(skill);
      return;
    }

    resetForm();
  }, [active, populateForm, resetForm, skill]);

  if (!active) {
    return null;
  }

  return (
    <Dialog
      onClose={closeDialog}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(26, 25, 22, 0.28)',
        },
      }}
      PaperProps={{
        sx: {
          width: 'calc(100vw - 24px)',
          maxWidth: '692px',
          m: '12px',
          borderRadius: '22px',
          border: '1px solid #EDE8E2',
          boxShadow: '0px 28px 90px rgba(26, 25, 22, 0.16)',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
        },
      }}
    >
      <Stack
        sx={{
          width: '100%',
        }}
      >
        <Stack
          sx={{
            px: {
              xs: '20px',
              sm: '26px',
            },
            pt: '16px',
            pb: '14px',
            gap: '10px',
          }}
        >
          <Stack
            direction='row'
            alignItems='flex-start'
            justifyContent='space-between'
            sx={{
              gap: '16px',
            }}
          >
            <Stack sx={{gap: '10px'}}>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 400,
                  color: '#A8A49E',
                  lineHeight: '100%',
                }}
              >
                <Translate expression='skills.skills.New.Eyebrow' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  fontWeight: 500,
                  color: '#1A1916',
                  lineHeight: '120%',
                }}
              >
                <Translate expression='skills.skills.New.Title' />
              </Typography>
            </Stack>
            <IconButton
              onClick={closeDialog}
              sx={{
                'width': '28px',
                'height': '28px',
                'borderRadius': '7px',
                'border': '1px solid #E8E4DE',
                'color': '#B0AAA2',
                'backgroundColor': '#FFFFFF',
                'flexShrink': 0,
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  borderColor: '#E8E4DE',
                },
              }}
            >
              <Close
                sx={{
                  fontSize: '16px',
                }}
              />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{borderBottom: '1px solid #EEE8E2'}}></Box>

        <Stack
          sx={{
            px: {
              xs: '20px',
              sm: '26px',
            },
            py: '18px',
            gap: '16px',
          }}
        >
          <SkillsNewName
            functionOptions={functionOptions}
            selectedFunction={selectedFunction}
            setSelectedFunction={setSelectedFunction}
            skillName={skillName}
            setSkillName={setSkillName}
          />
          <SkillsNewPrompt
            prompt={prompt}
            setParameters={setParameters}
            setPrompt={setPrompt}
          />
          <SkillsNewGroup
            existingGroup={existingGroup}
            groupMode={groupMode}
            groupOptions={groupOptions}
            newGroupName={newGroupName}
            setExistingGroup={setExistingGroup}
            setGroupMode={setGroupMode}
            setNewGroupName={setNewGroupName}
          />
        </Stack>

        <Box sx={{borderBottom: '1px solid #EEE8E2'}}></Box>

        <Stack
          direction='row'
          justifyContent='flex-end'
          sx={{
            px: {
              xs: '20px',
              sm: '26px',
            },
            py: '12px',
            gap: '10px',
          }}
        >
          <Button
            onClick={closeDialog}
            variant='outlined'
            disableElevation
            sx={{
              'borderRadius': '7px',
              'px': '10px',
              'py': '10px',
              'fontSize': '12px',
              'fontWeight': 400,
              'lineHeight': '100%',
              'textTransform': 'none',
              'color': '#6B6760',
              'borderColor': '#E8E4DE',
              'backgroundColor': '#FFFFFF',
              '&:hover': {
                backgroundColor: '#FFFFFF',
                borderColor: '#E8E4DE',
                boxShadow: 'none',
              },
            }}
          >
            <Translate expression='skills.skills.New.Cancel' />
          </Button>
          <Button
            onClick={handleSubmit}
            disableElevation
            disabled={isSubmitDisabled}
            sx={{
              'borderRadius': '7px',
              'px': '10px',
              'py': '10px',
              'fontSize': '12px',
              'fontWeight': 500,
              'lineHeight': '100%',
              'textTransform': 'none',
              'color': '#FFFFFF',
              'backgroundColor': '#DA7955',
              '&:hover': {
                backgroundColor: '#DA7955',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E8B19D',
                color: '#FFFFFF',
              },
            }}
          >
            <Translate expression={submitButtonExpression} />
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

SkillsNew.propTypes = {
  active: PropTypes.bool.isRequired,
  groupOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  handleCreateSkill: PropTypes.func.isRequired,
  skill: PropTypes.shape({
    groupName: PropTypes.string,
    name: PropTypes.string,
    outputType: PropTypes.string,
    parameters: PropTypes.array,
    prompt: PropTypes.string,
    skillRn: PropTypes.string,
  }),
};

export default React.memo(SkillsNew);
